/**
 * AnalyticsProvider — React context provider that initializes the analytics manager.
 * Wraps the app and provides auto-tracking (page views, time-on-page, scroll depth).
 */

import React, { createContext, useContext, useEffect, useRef } from 'react';
import analytics from './manager';
import { usePageTracking, useTimeOnPage, useScrollDepth } from './hooks';

const AnalyticsContext = createContext(null);

/**
 * Internal component for auto-tracking hooks.
 * Must be inside a Router for useLocation().
 */
function AutoTracking() {
  usePageTracking();
  useTimeOnPage();
  useScrollDepth();
  return null;
}

/**
 * AnalyticsProvider initializes the analytics manager and wraps the app.
 *
 * @param {Object} props
 * @param {Object} props.config - Analytics configuration (providers, debug, etc.)
 * @param {React.ReactNode} props.children
 */
export default function AnalyticsProvider({ config, children }) {
  const initialized = useRef(false);

  useEffect(() => {
    if (!initialized.current && config) {
      analytics.init(config);
      initialized.current = true;
    }

    return () => {
      analytics.shutdown();
      initialized.current = false;
    };
  }, [config]);

  return (
    <AnalyticsContext.Provider value={analytics}>
      {children}
    </AnalyticsContext.Provider>
  );
}

/**
 * AutoTrackingWrapper — renders AutoTracking only when inside a Router.
 * Place this inside your Router, after AnalyticsProvider.
 */
export function AnalyticsAutoTracking() {
  return <AutoTracking />;
}

export function useAnalyticsContext() {
  return useContext(AnalyticsContext);
}
